import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import Typography from 'Components/UI/Typography';
import Workspaces from 'Components/AdvisorApp/Viewport/Preferences/Workspaces';
import Button from 'Components/UI/Button';
import { QuestionAnswer, Work } from '@material-ui/icons';
import MessagesPreferences from 'Components/AdvisorApp/Viewport/Preferences/MessagesPreferences';
import featureEnabled from 'Helpers/Affordances';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import { addQuery, QueryVal, removeQuery } from 'Helpers/Query';
import useMobileLayout from 'Hooks/useMobileLayout';
import BackToPreferencesButton from './BackToPreferencesButton';

const styles = (theme) => ({
  root: {
    display: 'flex',
    width: '100%',
  },
  rootMobile: {
    display: 'flex',
    width: '100%',
    height: '100vh',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.facet.primaryBlue,
    width: theme.spacing(45),
  },
  sidebarMobile: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.facet.primaryBlue,
    width: '100%',
  },
  sideBarMobileHidden: {
    display: 'none',
  },
  header: {
    paddingBottom: theme.spacing(2),
  },
  button: {
    display: 'flex',
    justifyContent: 'flex-start',
    height: theme.spacing(8),
    marginBottom: theme.spacing(2),
    fontSize: theme.typography.h2.fontSize,
  },
  icon: {
    fontSize: theme.typography.h1.fontSize,
    marginRight: theme.spacing(4),
  },
  content: {},
});

const Preferences = ({
  className,
  classes,
}) => {
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Preferences',
    isPageView: true,
  });
  const { userResource, agentResource } = useUserContext();
  const isMobile = useMobileLayout();
  const notificationPreferencesEnabled = featureEnabled(agentResource, fr.relationships.notificationPreference.name);
  const workspacesEnabled = featureEnabled(userResource, fr.relationships.workspaces.name);
  const preferenceOptions = [
    {
      name: 'Workspaces', queryVal: 'workspaces', test: 'settings-preferences-workspaces-button', icon: Work, component: Workspaces, enabled: workspacesEnabled,
    },
    {
      name: 'Messages', queryVal: 'messages', test: 'settings-preferences-messages-button', icon: QuestionAnswer, component: MessagesPreferences, enabled: notificationPreferencesEnabled,
    },
  ];

  const queryPreferenceVal = QueryVal('preference');
  const [selectedPreference, setSelectedPreference] = useState(preferenceOptions.find(o => o.enabled && o.queryVal === queryPreferenceVal)?.name);
  const PreferenceTab = preferenceOptions.find(o => o.name === selectedPreference)?.component;

  useEffect(() => {
    if (queryPreferenceVal) {
      setSelectedPreference(preferenceOptions.find(o => o.enabled && o.queryVal === queryPreferenceVal)?.name);
    }
  }, [agentResource?.id()]);

  const selectPreference = (option) => {
    setSelectedPreference(option.name);
    addQuery({ preference: option.queryVal });
  };

  const removePreference = () => {
    setSelectedPreference(null);
    removeQuery('preference');
  };

  const renderPreferenceTab = () => {
    if (isMobile && PreferenceTab) {
      return (
        <>
          <BackToPreferencesButton onClick={removePreference} />
          <PreferenceTab />
        </>
      );
    }

    if (PreferenceTab) {
      return <PreferenceTab />;
    }

    return null;
  };

  return (
    <div
      className={classNames(className,
        classNames({
          [classes.root]: !isMobile,
          [classes.rootMobile]: isMobile,
        }))}
      {...rootNodeProps}
    >
      <div className={classNames({
        [classes.sidebar]: !isMobile,
        [classes.sidebarMobile]: isMobile,
        [classes.sideBarMobileHidden]: isMobile && PreferenceTab,
      })}
      >
        <Typography
          className={classes.header}
          variant="h1"
          color="white"
        >
          Preferences
        </Typography>
        {preferenceOptions.map((option) => (option.enabled ? (
          <Button
            key={option.name}
            className={classes.button}
            color={'primaryBlue'}
            variant={'contained'}
            test={option.test}
            onClick={() => selectPreference(option)}
          >
            <option.icon className={classes.icon} />
            {option.name}
          </Button>
        ) : (
          <></>
        )))}
      </div>
      <div className={classes.content}>
        {renderPreferenceTab()}
      </div>
    </div>
  );
};

Preferences.defaultProps = {
  className: '',
};

Preferences.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Preferences);
